export type QuickMessageGroup = {
  id: string;
  name: string;
  order: number;
};

export type QuickMessageItem = {
  id: string;
  text: string;
  groupId: string | null;
  order: number;
  immediateSend: boolean;
};

export type QuickMessageConfig = {
  groups: QuickMessageGroup[];
  items: QuickMessageItem[];
};

export type QuickMessageImportResult = {
  config: QuickMessageConfig;
  addedGroups: number;
  addedItems: number;
  skippedItems: number;
  limitedItems: number;
};

export class QuickMessageImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'QuickMessageImportError';
  }
}

export const DEFAULT_QUICK_MESSAGE_GROUP_ID = '';
export const DEFAULT_QUICK_MESSAGE_GROUP_NAME = '未分组';

export function normalizeQuickMessageText(text = ''): string {
  return String(text || '').replace(/\r\n/g, '\n').trim();
}

export function createQuickMessageId(prefix: 'grp' | 'qm'): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeGroupId(groupId: unknown): string | null {
  const value = String(groupId || '').trim();
  return value && value !== DEFAULT_QUICK_MESSAGE_GROUP_ID ? value : null;
}

function asOrder(value: unknown, fallback: number): number {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

export function normalizeQuickMessageConfig(input: any, limit = 24): QuickMessageConfig {
  const rawGroups = Array.isArray(input?.groups) ? input.groups : [];
  const groups: QuickMessageGroup[] = [];
  const groupIds = new Set<string>();

  rawGroups.forEach((group: any, index: number) => {
    const id = String(group?.id || '').trim();
    const name = String(group?.name || '').trim().slice(0, 40);
    if (!id || !name || groupIds.has(id)) return;
    groups.push({ id, name, order: asOrder(group?.order, index + 1) });
    groupIds.add(id);
  });
  groups.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name, 'zh-CN'));

  const sourceItems = Array.isArray(input)
    ? input
    : Array.isArray(input?.items)
      ? input.items
      : [];
  const seenText = new Set<string>();
  const items: QuickMessageItem[] = [];

  sourceItems.forEach((rawItem: any, index: number) => {
    const isObject = rawItem && typeof rawItem === 'object';
    const text = normalizeQuickMessageText(isObject ? rawItem.text : rawItem);
    if (!text || seenText.has(text)) return;
    seenText.add(text);
    const rawGroupId = isObject ? normalizeGroupId(rawItem.groupId) : null;
    items.push({
      id: String(isObject ? rawItem.id || '' : '').trim() || createQuickMessageId('qm'),
      text,
      groupId: rawGroupId && groupIds.has(rawGroupId) ? rawGroupId : null,
      order: asOrder(isObject ? rawItem.order : undefined, index + 1),
      immediateSend: isObject ? rawItem.immediateSend === true : false,
    });
  });
  items.sort((a, b) => a.order - b.order || a.text.localeCompare(b.text, 'zh-CN'));

  return { groups, items: items.slice(0, limit) };
}

export function serializeQuickMessageConfig(config: QuickMessageConfig, limit = 24): QuickMessageConfig {
  return normalizeQuickMessageConfig(config, limit);
}

function assertImportObject(value: any, path: string): void {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new QuickMessageImportError(`${path} 必须是对象`);
  }
}

function assertOptionalString(value: any, path: string): void {
  if (value !== undefined && typeof value !== 'string') throw new QuickMessageImportError(`${path} 必须是字符串`);
}

function assertOptionalFiniteNumber(value: any, path: string): void {
  if (value !== undefined && (typeof value !== 'number' || !Number.isFinite(value))) {
    throw new QuickMessageImportError(`${path} 必须是有限数字`);
  }
}

// 严格校验导入文件；结构错误直接失败，避免半成功导入污染当前配置。
function validateImportConfig(input: any): QuickMessageConfig {
  if (Array.isArray(input)) {
    if (input.length === 0 || input.some((item) => typeof item !== 'string' || !normalizeQuickMessageText(item))) {
      throw new QuickMessageImportError('快捷消息数组必须包含非空字符串');
    }
    return normalizeQuickMessageConfig(input, Number.MAX_SAFE_INTEGER);
  }
  assertImportObject(input, '导入内容');
  if (!Array.isArray(input.groups) || !Array.isArray(input.items)) {
    throw new QuickMessageImportError('导入内容必须包含 groups 和 items 数组');
  }

  input.groups.forEach((group: any, index: number) => {
    assertImportObject(group, `groups[${index}]`);
    assertOptionalString(group.id, `groups[${index}].id`);
    if (typeof group.name !== 'string' || !normalizeQuickMessageText(group.name)) {
      throw new QuickMessageImportError(`groups[${index}].name 必须是非空字符串`);
    }
    assertOptionalFiniteNumber(group.order, `groups[${index}].order`);
  });
  input.items.forEach((item: any, index: number) => {
    assertImportObject(item, `items[${index}]`);
    assertOptionalString(item.id, `items[${index}].id`);
    if (typeof item.text !== 'string' || !normalizeQuickMessageText(item.text)) {
      throw new Error(`items[${index}].text 必须是非空字符串`);
    }
    assertOptionalString(item.groupId, `items[${index}].groupId`);
    assertOptionalFiniteNumber(item.order, `items[${index}].order`);
    if (item.immediateSend !== undefined && typeof item.immediateSend !== 'boolean') {
      throw new QuickMessageImportError(`items[${index}].immediateSend 必须是布尔值`);
    }
  });
  const groupNames = new Set<string>();
  input.groups.forEach((group: any) => {
    const name = normalizeQuickMessageText(group.name);
    if (groupNames.has(name)) throw new QuickMessageImportError(`导入文件包含重复分组：${name}`);
    groupNames.add(name);
  });
  return normalizeQuickMessageConfig(input, Number.MAX_SAFE_INTEGER);
}

export function importQuickMessageConfig(
  current: QuickMessageConfig,
  input: unknown,
  limit = 24,
): QuickMessageImportResult {
  // 按分组名称合并，保留本地已有消息；新增项按文件顺序加入并重新生成本地 ID。
  const imported = validateImportConfig(input);
  const next = normalizeQuickMessageConfig(current, limit);
  const groupsByName = new Map(next.groups.map((group) => [group.name, group]));
  const importedGroupIds = new Map<string, string | null>();
  let nextGroupOrder = getNextQuickMessageGroupOrder(next.groups);
  let addedGroups = 0;

  imported.groups.forEach((group) => {
    const existing = groupsByName.get(group.name);
    if (existing) {
      importedGroupIds.set(group.id, existing.id);
      return;
    }
    const created = { id: createQuickMessageId('grp'), name: group.name, order: nextGroupOrder++ };
    next.groups.push(created);
    groupsByName.set(created.name, created);
    importedGroupIds.set(group.id, created.id);
    addedGroups += 1;
  });

  const existingTexts = new Set(next.items.map((item) => item.text));
  const importedTexts = new Set<string>();
  let skippedItems = 0;
  let limitedItems = 0;
  const remaining = Math.max(0, limit - next.items.length);
  imported.items.forEach((item, index) => {
    const text = normalizeQuickMessageText(item.text);
    if (existingTexts.has(text) || importedTexts.has(text)) {
      skippedItems += 1;
      return;
    }
    if (importedTexts.size >= remaining) {
      limitedItems += 1;
      return;
    }
    importedTexts.add(text);
    const groupId = item.groupId ? importedGroupIds.get(item.groupId) || null : null;
    next.items.push({
      id: createQuickMessageId('qm'),
      text,
      groupId: groupId || null,
      order: next.items.length + index + 1,
      immediateSend: item.immediateSend === true,
    });
  });

  const addedItems = importedTexts.size;
  return {
    config: normalizeQuickMessageConfig(next, limit),
    addedGroups,
    addedItems,
    skippedItems,
    limitedItems,
  };
}

export function getNextQuickMessageOrder(items: QuickMessageItem[], groupId: string | null): number {
  const matching = items.filter((item) => (item.groupId || null) === (groupId || null));
  const minOrder = matching.reduce((min, item) => Math.min(min, Number(item.order || 0)), 0);
  return minOrder - 1;
}

export function getNextQuickMessageGroupOrder(groups: QuickMessageGroup[]): number {
  return groups.reduce((max, group) => Math.max(max, Number(group.order || 0)), 0) + 1;
}
